import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { useStoreLocatorShell } from '../hooks/useStoreLocatorShell';
import type { CityFilterableService } from '../types/physicalStoreServiceContracts';
import type { StoreCityGroupVm } from '../viewModels/physicalStore.vm';
import { facebookFindLocation } from '../analytics/facebookFindLocation';
import { StoreCityFilter } from './StoreCityFilter';
import { StoreCitySidebar } from './StoreCitySidebar';
import { StoreFeatureSections } from './StoreFeatureSections';
import { StoreHero } from './StoreHero';
import { StoreListMobile } from './StoreListMobile';
import { StoreMapCard } from './StoreMapCard';

type StoreLocatorShellProps = {
  service: CityFilterableService;
  cityGroups: StoreCityGroupVm[];
  isLoading: boolean;
  storeCount: number | null;
  /** Preferred city to select on load (defaults to New Delhi when omitted). */
  initialCityName?: string;
};

export function StoreLocatorShell({
  service,
  cityGroups,
  isLoading,
  storeCount,
  initialCityName,
}: StoreLocatorShellProps) {
  const isDesktop = useIsDesktop();
  const {
    cities,
    effectiveSelectedCity,
    effectiveMobileFilter,
    filteredMobileGroups,
    selectedCityStores,
    handleStoreSelect,
    handleSelectCity,
    handleMobileFilterChange,
  } = useStoreLocatorShell({ service, cityGroups, initialCityName });

  const findLocationResultsFiredRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (findLocationResultsFiredRef.current) return;
    findLocationResultsFiredRef.current = true;
    facebookFindLocation({});
  }, [isLoading]);

  return (
    <div className="w-full bg-white">
      <StoreHero storeCount={storeCount} />

      {isLoading ? (
        <div className="flex items-center justify-center py-16" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-[#4A7B9D]" aria-hidden />
          <span className="sr-only">Loading stores</span>
        </div>
      ) : cityGroups.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">No stores found.</p>
      ) : isDesktop ? (
        <div className="mx-auto flex max-w-6xl gap-0 px-4 pb-12 md:min-h-[500px]">
          <div className="w-1/4 shrink-0">
            <StoreCitySidebar
              cities={cities}
              selectedCity={effectiveSelectedCity}
              onSelectCity={handleSelectCity}
            />
          </div>
          <div className="w-3/4 overflow-y-auto pl-6">
            {selectedCityStores.map((store) => (
              <StoreMapCard
                key={store.sid || store.id}
                store={store}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <StoreCityFilter
            cities={cities}
            selectedCity={effectiveMobileFilter}
            onChange={handleMobileFilterChange}
          />
          <StoreListMobile
            cityGroups={filteredMobileGroups}
            onStoreSelect={handleStoreSelect}
          />
        </>
      )}

      <StoreFeatureSections />
    </div>
  );
}
